import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { motion, useViewportScroll, useTransform } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const Hero: React.FC = () => {
  const { scrollY } = useViewportScroll();
  const [parallax, setParallax] = useState(0);

  // Detekcija mobilnog uređaja
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    // Učitaj NeetoCal skriptu samo jednom
    if (!document.getElementById('neetocal-embed-js')) {
      const script = document.createElement('script');
      script.id = 'neetocal-embed-js';
      script.async = true;
      script.src = 'https://synorio.neetocal.com/javascript/embed.js';
      document.body.appendChild(script);
    }
    // Inicijalizuj embed nakon što je skripta učitana
    const interval = setInterval(() => {
      if (window.neetoCal && window.neetoCal.embed) {
        window.neetoCal.embed({
          type: 'elementClick',
          id: '220f06ba-8982-4c38-8cb9-f73e8216078c',
          organization: 'synorio',
          elementSelector: '#open-popup-button',
        });
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return scrollY.onChange((v) => {
      if (!isMobile) setParallax(v);
      else setParallax(0);
    });
  }, [scrollY, isMobile]);

  return (
    <div id="hero-section" className="relative min-h-[90dvh] sm:min-h-[75vh] flex flex-col items-center justify-center text-center px-2 sm:px-4 py-10 sm:py-0">
      {/* Animated blobs for visual interest with parallax */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div 
          className="absolute top-4 left-1/4 w-56 h-56 sm:w-64 sm:h-64 rounded-full bg-synorio-purple/10 blur-[32px] sm:blur-[48px]"
          style={{ willChange: 'transform', y: parallax * 0.08 }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 20, 0],
            y: [0, -10, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
        />
        <motion.div 
          className="absolute bottom-4 right-1/4 w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-synorio-purple/20 blur-[36px] sm:blur-[60px]"
          style={{ willChange: 'transform', y: parallax * 0.12 }}
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.15, 0.25, 0.15],
            x: [0, -20, 0],
            y: [0, 15, 0]
          }}
          transition={{ duration: 14, repeat: Infinity, repeatType: "reverse", delay: 2 }}
        />
        {/* Noise overlay with fade-in */}
        <motion.div className="absolute inset-0 z-20 pointer-events-none noise-overlay" style={{ filter: 'blur(2px)' }} initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} transition={{ duration: 2 }} />
      </div>

      {/* Main content sa suptilnim glass panelom */}
      <motion.div 
        className="relative z-30 w-full max-w-2xl sm:max-w-4xl mx-auto px-2 sm:px-8 py-8 sm:py-10 md:px-16 md:py-16 rounded-2xl sm:rounded-3xl glass-panel-optimized shadow-2xl"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        {/* Decorative element */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="flex justify-center mb-4 sm:mb-6"
        >
          <div className="relative">
            <Sparkles className="text-synorio-purple w-5 h-5 sm:w-6 sm:h-6" />
            <motion.div
              className="absolute inset-0 rounded-full bg-synorio-purple/20 blur-md"
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.7, 0.3, 0.7]
              }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
            />
          </div>
        </motion.div>

        <motion.h1 
          className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-synorio-purple to-synorio-purple/80 drop-shadow-lg"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-synorio-purple/80">
            Razvijamo AI, analitička i data inženjerska rešenja koja donose rezultate
          </span>
        </motion.h1>
        
        <motion.p 
          className="text-xl sm:text-2xl text-gray-300 mb-8 sm:mb-12 font-medium max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Želimo da čujemo vaše poslovne izazove
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mb-6 sm:mb-8"
        >
          <Button 
            id="open-popup-button"
            size="lg" 
            className="relative group overflow-hidden bg-gradient-to-r from-synorio-purple to-synorio-darkpurple hover:from-synorio-darkpurple hover:to-synorio-purple transition-all duration-500 px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg"
          >
            <span className="relative z-10">Zakaži besplatan sastanak</span>
            <motion.div 
              className="absolute inset-0 bg-white/10"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.5 }}
            />
          </Button>
          <p className="text-sm text-gray-400 mt-2">30 minuta, bez prodaje. Samo konkretan razgovor.</p>
        </motion.div>

        {/* Suptilni scroll indikator */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 0.7, y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
          className="flex flex-col items-center justify-center mt-8 cursor-pointer"
          onClick={() => {
            const el = document.querySelector('#partners-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
          aria-label="Scrolluj nadole"
        >
          <div className="rounded-full bg-white/10 border border-synorio-purple/30 p-2 shadow-lg">
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="text-synorio-purple">
              <path d="M12 5v14m0 0l-6-6m6 6l6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Hero;
